import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Search, MapPin, Briefcase, DollarSign, Clock, 
  Building, Filter, Loader2, ChevronRight, ArrowLeft 
} from 'lucide-react';
import { COUNTRIES } from '../utils/locationData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const JobBoardPage = () => {
  const navigate = useNavigate();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedJobType, setSelectedJobType] = useState('');
  const [selectedWorkLocation, setSelectedWorkLocation] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [limit] = useState(20);

  useEffect(() => {
    fetchJobs();
  }, [page, selectedCountry, selectedJobType, selectedWorkLocation]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit
      });
      
      if (selectedCountry) params.append('country', selectedCountry);
      if (selectedJobType) params.append('job_type', selectedJobType);
      if (selectedWorkLocation) params.append('work_location', selectedWorkLocation);
      
      const response = await fetch(`${BACKEND_URL}/api/jobs/public/list?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchJobs();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCountry('');
    setSelectedJobType('');
    setSelectedWorkLocation('');
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    const days = Math.floor((new Date() - new Date(dateString)) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return `${Math.floor(days / 30)} months ago`;
  };

  const filteredJobs = searchTerm
    ? jobs.filter(job => 
        job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : jobs;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Back to Home Button */}
      <div className="bg-white border-b border-slate-200 py-3">
        <div className="max-w-7xl mx-auto px-6">
          <Button 
            onClick={() => navigate('/')} 
            variant="outline"
            size="sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          {/* Hiring Banner for Employers */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Briefcase className="w-6 h-6" />
                <div>
                  <p className="font-semibold text-lg">Are you hiring?</p>
                  <p className="text-blue-100 text-sm">Post your job for free and reach thousands of candidates</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-white text-blue-600 hover:bg-blue-50"
              >
                Post a Job - Sign In
              </Button>
            </div>
          </div>

          <h1 className="text-4xl font-bold mb-2">Find Your Next Opportunity</h1>
          <p className="text-blue-100 text-lg mb-8">
            Browse {total} active job postings from top recruiters
          </p>
          
          {/* Search Bar */}
          <div className="flex gap-3 max-w-3xl">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search job titles, companies, or keywords..."
                className="w-full pl-12 pr-4 py-4 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <Button 
              onClick={handleSearch}
              className="bg-white text-blue-600 hover:bg-blue-50 px-8"
            >
              Search
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-900 flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Filters
                  </h3>
                  {(selectedCountry || selectedJobType || selectedWorkLocation) && (
                    <button
                      onClick={clearFilters}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Country Filter */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Country
                    </label>
                    <select
                      value={selectedCountry}
                      onChange={(e) => {
                        setSelectedCountry(e.target.value);
                        setPage(0);
                      }}
                      className="w-full p-2 border rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                    >
                      <option value="">All Countries</option>
                      {COUNTRIES.map(country => (
                        <option key={country.value} value={country.value}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Job Type Filter */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Job Type
                    </label>
                    <select
                      value={selectedJobType}
                      onChange={(e) => {
                        setSelectedJobType(e.target.value);
                        setPage(0);
                      }}
                      className="w-full p-2 border rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                    >
                      <option value="">All Types</option>
                      <option value="full_time">Full-time</option>
                      <option value="part_time">Part-time</option>
                      <option value="contract">Contract</option>
                      <option value="internship">Internship</option>
                      <option value="temporary">Temporary</option>
                    </select>
                  </div>

                  {/* Work Location Filter */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Work Location
                    </label>
                    <select
                      value={selectedWorkLocation}
                      onChange={(e) => {
                        setSelectedWorkLocation(e.target.value);
                        setPage(0);
                      }}
                      className="w-full p-2 border rounded-lg text-sm focus:border-blue-500 focus:outline-none"
                    >
                      <option value="">All Locations</option>
                      <option value="remote">Remote</option>
                      <option value="hybrid">Hybrid</option>
                      <option value="onsite">Onsite</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Jobs List */}
          <div className="lg:col-span-3">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                {filteredJobs.length} {filteredJobs.length === 1 ? 'Job' : 'Jobs'} Found
              </h2>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
              </div>
            ) : filteredJobs.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Jobs Found</h3>
                  <p className="text-slate-600 mb-4">
                    Try adjusting your filters or search terms
                  </p>
                  <Button onClick={clearFilters} variant="outline">
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredJobs.map(job => (
                  <Card 
                    key={job.job_id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/jobs/${job.job_id}`)}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-slate-900 mb-2 hover:text-blue-600">
                            {job.title}
                          </h3>
                          
                          <div className="flex items-center gap-2 text-slate-700 mb-3">
                            <Building className="w-4 h-4" />
                            <span className="font-semibold">{job.company_name}</span>
                          </div>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-4">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.city ? `${job.city}, ` : ''}{job.country}
                            </div>
                            
                            <div className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4" />
                              {job.job_type?.replace('_', '-')}
                            </div>
                            
                            <Badge variant="outline" className="text-xs">
                              {job.work_location}
                            </Badge>
                            
                            {job.salary_min && (
                              <div className="flex items-center gap-1">
                                <DollarSign className="w-4 h-4" />
                                {job.salary_currency} {job.salary_min.toLocaleString()}
                                {job.salary_max && ` - ${job.salary_max.toLocaleString()}`}
                              </div>
                            )}
                          </div>

                          <p className="text-slate-600 line-clamp-2 mb-3">
                            {job.description}
                          </p>

                          {job.skills && job.skills.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                              {job.skills.slice(0, 5).map((skill, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {skill}
                                </Badge>
                              ))}
                              {job.skills.length > 5 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{job.skills.length - 5} more
                                </Badge>
                              )}
                            </div>
                          )}

                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Clock className="w-4 h-4" />
                            Posted {formatDate(job.published_at)}
                          </div>
                        </div>

                        <ChevronRight className="w-6 h-6 text-slate-400 flex-shrink-0 ml-4" />
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Pagination */}
                {total > limit && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <Button
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      variant="outline"
                    >
                      Previous
                    </Button>
                    
                    <span className="text-sm text-slate-600 px-4">
                      Page {page + 1} of {Math.ceil(total / limit)}
                    </span>
                    
                    <Button
                      onClick={() => setPage(p => p + 1)}
                      disabled={(page + 1) * limit >= total}
                      variant="outline"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobBoardPage;
